import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, DialogContent, DialogTitle, Typography, useTheme} from "@mui/material";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import {ReactComponent as Excel} from "../../../../assets/images/export.svg";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import Form from "../../base/form";
import ToggleButtons from "../../base/button/toggle_buttons";
import LineChart from "../../base/chart/line-chart";
import Utils, {exportCSVFile} from "../../../../core/services/utils/utils";
import ToastService from "../../../../core/services/toast";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Grid2 from "@mui/material/Unstable_Grid2";

const formValues = {
    startDate: 'startDate',
    endDate: 'endDate',
}


const initConstraints = {
    [formValues.startDate]: null,
    [formValues.endDate]: null,
};

const ProductEntryHistoryDialog = ({
                                       title,
                                       open,
                                       close,
                                       getHistory,
                                       hideExport,
                                       getHistoryForExport,
                                       valueAggregator,
                                   }) => {
    const [exporting, setExporting] = useState(false);
    const [size, setSize] = useState('md');
    const [loading, setLoading] = useState(false);
    const [dataset, setDataset] = useState([]);
    const [constraints, setConstraints] = useState(initConstraints);
    const isMounted = useIsMounted();
    const initHistoryFetchingRef = useRef(false);

    /**
     * With each change in the dialog being open or closed:
     * Sets the [initHistoryFetchingRef]'s current value to the open in the state
     */
    useEffect(() => {
        initHistoryFetchingRef.current = open;
    }, [open])

    /**
     * Fetches the given product's entry history using the specified date constraint.
     *
     * @param {any} constraints
     * @return {Promise<void>}
     */
    const getProductEntryHistory = useCallback(async (constraints) => {
        const forApi = {
            startDate: constraints[formValues.startDate],
            endDate: constraints[formValues.endDate],
        }
        setLoading(true);
        const response = await getHistory(forApi);
        if (!isMounted()) return;
        if (
            initHistoryFetchingRef.current &&
            (
                !(response.data?.length ?? 0) ||
                (response.data?.length === 1 && Object.keys(response.data[0]).length === 1))
        ) {
            initHistoryFetchingRef.current = false;
            close();
            ToastService.error({
                title: "Not Found",
                message: "No data entries were found to populate the chart. Please contact IT support",
            });
            return;
        }
        setLoading(false);
        if (response?.resultFlag) {
            setDataset(response.data);
        }
    }, [close, getHistory, isMounted])

    /**
     * with each change in open and constants:
     * - if not open, then resets the constraints
     * - if open, then fetches the product history data.
     */
    useEffect(() => {
        if (!open) {
            setConstraints(initConstraints);
            setDataset([]);
            return;
        }
        getProductEntryHistory(constraints).then();
    }, [open, constraints, getProductEntryHistory])


    /**
     * Exports the product cost history.
     *
     * * this method does not take into account the current visibility type.
     * * this method will use the existing constraining filters for its search.
     * @return {Promise<void>}
     */
    const exportData = useCallback(async () => {
        const forApi = {
            startDate: constraints[formValues.startDate],
            endDate: constraints[formValues.endDate],
        }
        setExporting(true);
        const response = await getHistoryForExport(forApi);
        if (!isMounted()) return;
        setExporting(false);

        if (!response?.resultFlag || !response.data?.length)
            return

        // export
        exportCSVFile(response.data, response?.title);
    }, [constraints, getHistoryForExport, isMounted])

    return (
        <>
            <Dialog
                open={!!open}
                className={'product-entry-history-dialog'}
                setOpen={close}
                maxWidth={size}
            >
                <DialogTitle>
                    <Row xs={12} className='no-gutters justify-content-center w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            {title ?? ""}
                        </p>
                    </Row>
                </DialogTitle>
                <DialogContent dividers className={'pb-0 px-0'}>
                    <div className={'d-flex flex-wrap'}>
                        {
                            !hideExport &&
                            <Col xs={12} className={'d-flex justify-content-end'}>
                                <Button
                                    disabled={!open || exporting || loading}
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={exportData}
                                >
                                    <div className={'icon-button-icon-container mr-2'}>
                                        <Excel/>
                                    </div>
                                    <p>
                                        {
                                            loading
                                                ? 'Loading'
                                                : exporting
                                                    ? 'Exporting...'
                                                    : "Convert to Excel"
                                        }
                                    </p>
                                </Button>
                            </Col>
                        }
                        <SearchSection
                            submit={(data) => setConstraints(data)}
                            loading={loading || exporting}
                        />
                        <ChartSection
                            dataset={dataset}
                            loading={loading}
                            constraints={constraints}
                            valueAggregator={valueAggregator}
                            setSize={setSize}
                        />
                    </div>
                </DialogContent>

            </Dialog>
        </>
    )
}


const schema = Yup.object().shape({
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan("End Date"))
        .nullable(),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan("Start Date"))
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const SearchSection = ({submit, loading}) => {

    const submitForm = useCallback((values) => {
        submit({
            ...values,
            [formValues.startDate]: values[formValues.startDate] ? DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat) : null,
            [formValues.endDate]: values[formValues.endDate] ? DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat) : null,
        });
    }, [submit])

    return (
        <Form
            className='w-100 mt-3'
            onSubmit={submitForm}
            validate={validate}
            initialValues={{}}
            render={({values}) => (
                <Box sx={{padding: 2}}>
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12}>
                            <Typography variant={'body2'} color={'text.secondary'} fontWeight={600}>
                                Search for a specific period:
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12} md={true}>
                            <MuiRFF.DatePicker
                                textFieldProps={{size: 'small'}}
                                name={formValues.startDate}
                                label={"Start Date"}
                                required={required[formValues.startDate]}
                                maxDate={values[formValues.endDate]}
                                disableFuture
                            />
                        </Grid2>
                        <Grid2 xs={12} md={true}>
                            <MuiRFF.DatePicker
                                textFieldProps={{size: 'small'}}
                                name={formValues.endDate}
                                label={"End Date"}
                                required={required[formValues.endDate]}
                                minDate={values[formValues.startDate]}
                                disableFuture
                            />
                        </Grid2>
                        <Grid2 xs={'auto'}>
                            <Button
                                disabled={loading}
                                className='py-2 px-8 m-auto'
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                            >
                                {loading ? 'Searching...' : 'Search'}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Box>
            )}
        />
    );
}

const visibilityTypes = {
    yearly: "yearly",
    monthly: "monthly",
    weekly: "weekly",
}

const ChartSection = ({dataset, loading, valueAggregator, setSize}) => {
    const [visibility, setVisibility] = useState(visibilityTypes.monthly);
    const visibilities = useMemo(() => [
        {
            value: visibilityTypes.yearly,
            name: "Yearly",
            disabled: loading
        },
        {
            value: visibilityTypes.monthly,
            name: "Monthly",
            disabled: loading
        },
        {
            value: visibilityTypes.weekly,
            name: "Weekly",
            disabled: loading
        }
    ], [loading])

    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])

    /**
     * With each change in the visibility:
     * - changes the size of the dialog appropriate to the expected number visual data in the chart.
     */
    useEffect(() => {
        switch (visibility) {
            case visibilityTypes.weekly:
                setSize('xl');
                break;
            case visibilityTypes.monthly:
                setSize('lg');
                break;
            case visibilityTypes.yearly:
            default:
                setSize('md');
                break;
        }
    }, [setSize, visibility])

    /**
     * Aggregates the values of the chart data that belong to the same key.
     *
     * * Currently, this function selects the last entry among the values.
     * @param {number[]} values
     * @return {number}
     */
    const defaultValueAggregator = (values) => {
        if (!values?.length)
            return 0;
        return values[values.length - 1];
    }

    /**
     * Get the string representation for the week of the month of a date.
     * @param {dayjs.Dayjs} date
     * @return {string}
     */
    const weekOfMonth = (date) => {
        const firstDayOfMonth = date.clone().startOf('month');
        const firstDayOfWeek = firstDayOfMonth.clone().startOf('week');
        const offset = firstDayOfMonth.diff(firstDayOfWeek, 'days');
        const week = Math.ceil((date.date() + offset) / 7);
        switch (week) {
            case 1:
                return '1st week';
            case 2:
                return '2nd week';
            case 3:
                return '3rd week';
            case 4:
                return '4th week';
            case 5:
                return '5th week';
            case 6:
                return '6th week';
            default:
                return `${week} week`;
        }
    }

    /**
     * Aggregates the data of a single dataset based on the current visibility.
     * @param {any[]} data
     * @return {{[p: string]: any}}
     */
    const getDatasetData = useCallback((data = []) => {
        const aggregated = {};
        switch (visibility) {
            case visibilityTypes.weekly: {
                data
                    ?.sort((a, b) => Utils.dateComparator(a.date, b.date))
                    ?.forEach(history => {
                        const mom = DateUtils.serverToLocal(history.date);
                        const date = weekOfMonth(mom) + ' of ' + mom.format('MMM YYYY');
                        aggregated[date] = [...(aggregated[date] ?? []), history.entry];
                    })
                break;
            }
            case  visibilityTypes.monthly: {
                data
                    ?.sort((a, b) => Utils.dateComparator(a.date, b.date))
                    ?.forEach(history => {
                        const date = DateUtils.serverToLocal(history.date).format('MMM YYYY');
                        aggregated[date] = [...(aggregated[date] ?? []), history.entry];
                    })
                break;
            }
            case  visibilityTypes.yearly: {
                data
                    ?.sort((a, b) => Utils.dateComparator(a.date, b.date))
                    ?.forEach(history => {
                        const date = DateUtils.serverToLocal(history.date).format('YYYY');
                        aggregated[date] = [...(aggregated[date] ?? []), history.entry];
                    })
                break;
            }
            default:
                break;
        }

        return Object.fromEntries(Object
            .entries(aggregated)
            .map(([date, values,]) => [date, (valueAggregator ?? defaultValueAggregator)(values)])
        );
    }, [valueAggregator, visibility]);

    /**@type {Partial<import('chart.js').ChartData>}*/
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        };
        dataset.forEach((datasetEntry) => {
            const data = getDatasetData(datasetEntry.data);
            chartData.datasets.push({
                data: data,

                label: datasetEntry.label,
                formatValue: datasetEntry.formatValue,

                borderColor: chartColors[0],
                pointBackgroundColor: chartColors[0],
                pointBorderColor: chartColors[0],
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                ...(datasetEntry.options ?? {})

            });
            chartData.labels = [...new Set([...chartData.labels, ...Object.keys(data)])];
        })
        return chartData;
    }, [dataset, getDatasetData, chartColors]);

    return (
        <>
            <Col xs={12}
                 className={'mt-4 mb-4 transition mx-auto'}>
                <LineChart height={window.innerHeight / 2} data={chartData}/>
            </Col>
            <Col xs={12} className={'d-flex justify-content-center mb-4'}>
                <ToggleButtons
                    value={visibility}
                    onChange={(_, v) => setVisibility(v)}
                    options={visibilities}
                />
            </Col>
        </>
    );
}

export default ProductEntryHistoryDialog;
